import { useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { getGravityUserId, getGravityUserOrgId } from '../../../hooks/useAuth';
import { useAuthStore } from '../../../stores/authStore';
import type { OrgPlanningScenarioReview } from '../models/OrgPlanningScenarioReview';
import {
  captureProtectedPlanningGeneration,
  subscribeProtectedPlanningInvalidation,
} from './protectedPlanningGeneration';
import {
  canReadOrgPlanningReview, orgPlanningReviewKey, sameOrgPlanningReviewScope,
  type OrgPlanningReviewScope,
} from './orgPlanningReviewKey';

export function captureOrgPlanningReviewScope(folderId: string, diagramId: string): OrgPlanningReviewScope {
  const generation = captureProtectedPlanningGeneration(folderId);
  return {
    folderId, diagramId,
    principalId: getGravityUserId(), actorOrgId: getGravityUserOrgId(),
    sessionGeneration: generation.session, folderGeneration: generation.folder,
  };
}

export function isOrgPlanningReviewScopeCurrent(scope: OrgPlanningReviewScope): boolean {
  return canReadOrgPlanningReview(scope)
    && sameOrgPlanningReviewScope(scope, captureOrgPlanningReviewScope(scope.folderId, scope.diagramId));
}

export function useOrgPlanningReviewScope(folderId: string, diagramId: string): OrgPlanningReviewScope {
  // Cookie identity is read through the shared auth helpers; subscribe to token
  // changes so replacing one authenticated account with another also rerenders.
  useAuthStore((state) => state.token);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const getGeneration = () => {
    const generation = captureProtectedPlanningGeneration(folderId);
    return `${generation.session}:${generation.folder}`;
  };
  useSyncExternalStore(subscribeProtectedPlanningInvalidation, getGeneration, getGeneration);
  const scope = captureOrgPlanningReviewScope(folderId, diagramId);
  return isAuthenticated ? scope : { ...scope, principalId: null, actorOrgId: null };
}

/** Never reintroduce a protected response after an account or policy purge. */
export function seedOrgPlanningReview(
  queryClient: QueryClient,
  scope: OrgPlanningReviewScope,
  changeSetId: string,
  review: OrgPlanningScenarioReview,
): boolean {
  if (!canReadOrgPlanningReview(scope) || review.changeSetId !== changeSetId
    || !sameOrgPlanningReviewScope(scope, captureOrgPlanningReviewScope(scope.folderId, scope.diagramId))) {
    return false;
  }
  queryClient.setQueryData(orgPlanningReviewKey(scope, changeSetId), review);
  return true;
}
